import { Timestamp } from "firebase/firestore";

// ============================================
// UTILITY TYPES
// ============================================

/**
 * Tipo para timestamps de Firestore que pueden venir como Timestamp o Date
 */
export type FirestoreTimestamp = Timestamp | Date | { toDate: () => Date };

/**
 * Tipo para errores de Firebase Auth
 */
export interface FirebaseAuthError extends Error {
  code?: string;
  message: string;
}

// ============================================
// BASE TYPES
// ============================================

export interface AuditFields {
  createdAt: Timestamp;
  createdBy?: string;
  updatedAt: Timestamp;
  updatedBy?: string;
}

export type Status = "active" | "inactive";
export type ClubType = "aventureros" | "conquistadores" | "guias_mayores";
export type DocumentType = "CC" | "TI" | "RC" | "CE" | "PASAPORTE" | "PPT";
/** @deprecated Usar el Gender del módulo Member */
export type LegacyGender = "M" | "F";

// Labels para tipos de club
export const CLUB_TYPE_LABELS: Record<ClubType, string> = {
  aventureros: "Aventureros",
  conquistadores: "Conquistadores",
  guias_mayores: "Guías Mayores",
};

// ============================================
// SYSTEM ROLES (NUEVO MODELO SIMPLIFICADO)
// ============================================

/**
 * Roles del sistema
 * - admin_union: Administrador de Unión (acceso total, gestiona asociaciones)
 * - admin_association: Administrador de Asociación (gestiona iglesias/clubes, asigna directores)
 * - club_director: Director de Club (gestiona UN club específico)
 * - member: Miembro registrado (solo ve su perfil y datos)
 */
export type SystemRole =
  | "admin_union"
  | "admin_association"
  | "club_director"
  | "member";

export const SYSTEM_ROLE_LABELS: Record<SystemRole, string> = {
  admin_union: "Administrador de Unión",
  admin_association: "Administrador de Asociación",
  club_director: "Director de Club",
  member: "Miembro",
};

export const SYSTEM_ROLE_LEVELS: Record<SystemRole, number> = {
  admin_union: 1,
  admin_association: 2,
  club_director: 3,
  member: 4,
};

/**
 * Tipos de scope para asignación de roles
 */
export type ScopeType = "union" | "association" | "club";

/**
 * Asignación de rol a un usuario
 * Vincula un usuario con un rol y un scope específico
 */
export interface RoleAssignment extends AuditFields {
  id: string;
  /** ID del usuario */
  userId: string;
  /** Email del usuario (para búsquedas rápidas) */
  userEmail: string;
  /** Nombre del usuario (para mostrar) */
  userName: string;
  /** Rol asignado */
  role: SystemRole;
  /** Tipo de scope */
  scopeType: ScopeType;
  /** ID del scope (unionId, associationId, o clubId) */
  scopeId: string;
  /** Nombre del scope (para mostrar) */
  scopeName: string;
  /**
   * Sección del club a la que aplica este rol (solo cuando scopeType=='club'
   * y se quiere acotar el rol a una sub-sección del club).
   *
   * - Si está ausente y role==='club_director' → director GENERAL del club
   *   (ve y administra todas las secciones).
   * - Si está presente y role==='club_director' → director SOLO de esa
   *   sección (ve y administra members con section/staffSection == X).
   *
   * Permite que un mismo club tenga al mismo tiempo un director general y
   * directores específicos por sección (Aventureros / Conquistadores / GM).
   */
  section?: ClubType;
  /** Estado de la asignación */
  status: "active" | "inactive";
  /** Quién asignó el rol */
  assignedBy: string;
  /** Fecha de asignación */
  assignedAt: Timestamp;
  /** Fecha de expiración (opcional) */
  expiresAt?: Timestamp;
  /** Notas */
  notes?: string;
}

/**
 * Usuario del sistema (todos los usuarios)
 * Se registran con email como login
 */
export interface SystemUser extends AuditFields {
  id: string;
  /** Email de login (único) */
  email: string;
  /** Nombre completo */
  displayName: string;
  /** URL de foto de perfil */
  photoUrl?: string;
  /** Teléfono de contacto */
  phone?: string;
  /** Estado del usuario */
  status: "active" | "inactive" | "pending";
  /** Último acceso */
  lastLoginAt?: Timestamp;
  /** Si debe cambiar contraseña en próximo login */
  mustChangePassword?: boolean;
  /** ID de la persona vinculada (si existe) */
  personId?: string;
}

/**
 * Configuración de una sección dentro de un club
 */
export interface SectionConfig {
  active: boolean;
  /** OJO (F4-1): aquí va el **personId**, no el uid — al revés que `Club.directorId`. */
  directorId?: string;
  /** uid de la cuenta del director de la sección (lo escribe assignSectionDirector). */
  directorUserId?: string;
  directorName?: string;
}

/**
 * Información básica de un club (para listas y referencias)
 */
export interface ClubInfo {
  id: string;
  name: string;
  /** @deprecated Usar sections */
  type?: ClubType;
  sections?: Record<ClubType, SectionConfig>;
  status: Status;
  memberCount?: number;
  associationId?: string;
  logoUrl?: string;
  inviteCode?: string;
}

// ============================================
// ORGANIZATION
// ============================================

export interface Union extends AuditFields {
  id: string;
  name: string;
  code: string;
  country: string;
  status: Status;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface Association extends AuditFields {
  id: string;
  unionId: string;
  name: string;
  code: string;
  type?: "association" | "mission";
  status: Status;
  region?: string;
  address?: string;
  contactPhone?: string;
  contactEmail?: string;
}

// NOTA: District se mantiene por compatibilidad pero es opcional
// La Asociación puede gestionar Iglesias directamente
export interface District extends AuditFields {
  id: string;
  associationId: string;
  name: string;
  code: string;
  status: Status;
  region?: string;
  pastorId?: string;
  contactPhone?: string;
  contactEmail?: string;
}

export interface Address {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

/**
 * Iglesia - Pertenece a una Asociación (opcionalmente a un Distrito)
 * Una iglesia tiene N clubes (típicamente 3: Aventureros, Conquistadores, Guías Mayores)
 */
export interface Church extends AuditFields {
  id: string;
  /** ID de la asociación (requerido) */
  associationId: string;
  /** ID del distrito (opcional - para organización interna) */
  districtId?: string;
  /** ID del distrito en el sistema externo (Alfoli). Usado como fallback cuando districtId es null. */
  alfoliDistrictId?: string;
  /** Nombre del distrito (desnormalizado para mostrar sin lookup extra) */
  districtName?: string;
  name: string;
  code: string;
  status: Status;
  pastorName?: string;
  address?: Address;
  contactPhone?: string;
  contactEmail?: string;
}

/**
 * Club - Entidad principal, puede tener hasta 3 secciones por rango de edad
 * Opcionalmente asociado a una Iglesia
 */
export interface Club extends AuditFields {
  id: string;
  /** @deprecated Usar como opcional — iglesia ya no es requerida */
  churchId?: string;
  churchName?: string;
  associationId?: string;
  periodId?: string;
  name: string;
  /** @deprecated Usar sections — mantenido para compatibilidad de lectura */
  type?: ClubType;
  /** Secciones del club (Aventureros, Conquistadores, Guías Mayores) */
  sections?: Record<ClubType, SectionConfig>;
  status: Status;
  /** URL del logo del club */
  logoUrl?: string;
  /** Ruta del logo en Storage (para poder eliminarlo) */
  logoPath?: string;
  /** ID del director general del club (userId) */
  directorId?: string;
  /** Nombre del director general (para mostrar) */
  directorName?: string;
  /** Código de invitación */
  inviteCode?: string;
  meetingDay?: string;
  meetingTime?: string;
  meetingPlace?: string;
  contactPhone?: string;
  contactEmail?: string;
  memberCount?: number;
  activeCount: number;
  unitCount: number;

  // ===== F11: CLUBES FUSIÓN (temporales, para eventos) =====
  /**
   * Club fusión: unión temporal de 2+ clubes reales para participar juntos en
   * un evento. Sus miembros se agregan de forma ADITIVA (conservan la
   * membresía de su club real, con `member.originClubId` apuntándolo) y al
   * disolverla se retiran de la fusión sin tocar los clubes de origen.
   */
  isFusion?: boolean;
  /** Ids de los clubes reales que componen la fusión. */
  fusionOf?: string[];

  /**
   * Tarifa especial ("zona rural") decidida CLUB por CLUB, por encima de su
   * distrito. `true` la concede, `false` la niega, ausente deja mandar al
   * distrito (`Event.discountedDistrictIds`), que es el caso normal.
   *
   * Nació de tres casos en dos días donde el distrito no alcanzaba: GAMA y
   * ARMOHET comparten la iglesia Filadelfia, BETANIA y JASER el distrito Pta
   * Bethania, y en ambos pares uno lleva la tarifa y el otro no. Con el
   * distrito como única fuente, dársela a uno se la daba al otro.
   *
   * Sólo un admin puede escribirlo (firestore.rules): para un director sería
   * un botón para rebajarse la inscripción.
   */
  hasSpecialRate?: boolean;
  /** Quién y cuándo lo decidió, para poder auditarlo. */
  specialRateSetBy?: string;
  specialRateSetAt?: Timestamp;
  /** Cuándo se disolvió (queda status inactive + este sello). */
  dissolvedAt?: Timestamp;

  // ===== DATOS PARA LA PLANILLA OFICIAL DE INSCRIPCIÓN =====
  /**
   * Tres datos que el encabezado de la planilla de la Asociación pide y que
   * la plataforma no modela en ninguna parte: la ZONA (agrupación por encima
   * del distrito), el PASTOR ASISTENTE y el MINISTRO. Los escribe el director
   * la primera vez que genera la planilla y quedan guardados para la
   * siguiente, que es lo único que los justifica como campos del club: no se
   * usan en ningún otro lado.
   */
  zone?: string;
  assistantPastor?: string;
  minister?: string;
}

export interface Unit extends AuditFields {
  id: string;
  clubId: string;
  periodId: string;
  name: string;
  motto?: string;
  color?: string;
  status: Status;
  leaderId?: string;
  memberCount: number;
}

export interface Period extends AuditFields {
  id: string;
  year: number;
  name: string;
  startDate: Timestamp;
  endDate: Timestamp;
  status: "upcoming" | "active" | "closed";
}

// ============================================
// PEOPLE
// ============================================

export interface Guardian {
  name: string;
  relationship: string;
  phone: string;
  email?: string;
}

/** @deprecated Ya no se usa - ahora los datos personales están en Person (nueva arquitectura) */
export interface LegacyPerson extends AuditFields {
  id: string;
  documentType: DocumentType;
  documentNumber: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  secondLastName?: string;
  fullName: string;
  birthDate: Timestamp;
  gender: LegacyGender;
  photoUrl: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  guardian?: Guardian;
  bloodType?: string;
  allergies?: string[];
  medicalConditions?: string[];
  eps?: string;
  status: Status;
}

export type MemberStatus = "active" | "inactive" | "pending" | "transferred";

/**
 * Rol de un miembro DENTRO de un club específico
 * - director: Director del club
 * - subdirector: Subdirector
 * - consejero: Consejero de unidad
 * - instructor: Instructor de clases/especialidades
 * - tesorero: Tesorero del club
 * - secretario: Secretario del club
 * - anciano: Anciano consejero (acompaña Guías Mayores)
 * - member: Miembro regular
 */
export type MemberRole =
  | "director"
  | "subdirector"
  | "consejero"
  | "instructor"
  | "tesorero"
  | "secretario"
  | "anciano"
  | "member";

export const MEMBER_ROLE_LABELS: Record<MemberRole, string> = {
  director: "Director",
  subdirector: "Subdirector",
  consejero: "Consejero",
  instructor: "Instructor",
  tesorero: "Tesorero",
  secretario: "Secretario",
  anciano: "Anciano",
  member: "Miembro",
};

/**
 * Clase/Nivel del miembro (aplica según tipo de club)
 */
export type MemberClass =
  // Aventureros
  | "abejita_laboriosa"
  | "rayito_de_sol"
  | "constructor"
  | "manos_ayudadoras"
  // Conquistadores
  | "amigo"
  | "companero"
  | "explorador"
  | "orientador"
  | "viajero"
  | "guia"
  // Guías Mayores
  | "guia_mayor"
  | "guia_mayor_master"
  | "guia_mayor_master_avanzado";

export const MEMBER_CLASS_LABELS: Record<MemberClass, string> = {
  // Aventureros
  abejita_laboriosa: "Abejita Laboriosa",
  rayito_de_sol: "Rayito de Sol",
  constructor: "Constructor",
  manos_ayudadoras: "Manos Ayudadoras",
  // Conquistadores
  amigo: "Amigo",
  companero: "Compañero",
  explorador: "Explorador",
  orientador: "Orientador",
  viajero: "Viajero",
  guia: "Guía",
  // Guías Mayores
  guia_mayor: "Guía Mayor",
  guia_mayor_master: "Guía Mayor Master",
  guia_mayor_master_avanzado: "Guía Mayor Master Avanzado",
};

/**
 * Historial de roles de un miembro
 */
export interface MemberRoleHistory {
  role: MemberRole;
  period: string; // Ej: "2025", "2025-2026"
  startDate: Timestamp;
  endDate?: Timestamp; // null = rol actual
}

export interface MemberStatusChange extends AuditFields {
  id: string;
  memberId: string;
  /** @deprecated Ya no se usa */
  personId?: string;
  clubId: string;
  previousStatus: MemberStatus;
  newStatus: MemberStatus;
  reason?: string;
  effectiveDate: Timestamp;
  changedBy: string;
}

/**
 * Membresía de una persona en un club
 * Un usuario puede tener múltiples membresías (en diferentes clubes)
 *
 * NOTA: personId y districtId son campos legacy que ya no se usan
 * - personId fue reemplazado por linkedUserId para vincular con usuarios
 * - districtId ya no se usa, las iglesias se conectan directo a asociaciones
 */
// Tipos para información personal del miembro
export type MemberType = "acampante" | "lider"; // Acampante o Líder
export type IdDocumentType = "CC" | "TI" | "CE" | "PASAPORTE" | "RC" | "PPT";
export type BloodType = "A+" | "A-" | "B+" | "B-" | "AB+" | "AB-" | "O+" | "O-";
export type Gender = "male" | "female";

export const MEMBER_TYPE_LABELS: Record<MemberType, string> = {
  acampante: "Acampante",
  lider: "Líder/Staff",
};

export const ID_DOCUMENT_TYPE_LABELS: Record<IdDocumentType, string> = {
  CC: "Cédula de Ciudadanía",
  TI: "Tarjeta de Identidad",
  CE: "Cédula de Extranjería",
  PASAPORTE: "Pasaporte",
  RC: "Registro Civil",
  /** Permiso por Protección Temporal (migrantes venezolanos en Colombia). */
  PPT: "Permiso por Protección Temporal (PPT)",
};

export const BLOOD_TYPE_LABELS: Record<BloodType, string> = {
  "A+": "A+",
  "A-": "A-",
  "B+": "B+",
  "B-": "B-",
  "AB+": "AB+",
  "AB-": "AB-",
  "O+": "O+",
  "O-": "O-",
};

export const GENDER_LABELS: Record<Gender, string> = {
  male: "Masculino",
  female: "Femenino",
};

// Información de contacto de emergencia
export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}

// ============================================
// PERSON (Persona física - datos personales únicos)
// ============================================

/**
 * Persona física con datos personales
 * Una persona puede tener múltiples membresías en diferentes clubes
 * pero sus datos personales se almacenan una sola vez aquí.
 *
 * Flujo:
 * 1. Director crea Person con datos personales
 * 2. Se crea Member vinculando Person a un Club
 * 3. Si la persona crea cuenta, se vincula Person.linkedUserId = User.id
 */
export interface Person extends AuditFields {
  id: string;

  // ===== IDENTIFICACIÓN =====
  firstName: string;
  lastName: string;
  fullName: string; // Generado: `${firstName} ${lastName}`
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;

  // ===== DATOS PERSONALES =====
  birthDate: Timestamp;
  gender: Gender;
  bloodType: BloodType;
  memberType: MemberType; // Acampante o Líder

  // ===== CONTACTO =====
  phone: string;
  email?: string;
  address?: string;
  emergencyContact: EmergencyContact;
  healthInsurance?: string; // Sisbén o EPS

  // ===== INFORMACIÓN MÉDICA (declarativa, lo escribe el participante o padre) =====
  /** Texto libre: enfermedades, condiciones crónicas declaradas. */
  medicalConditions?: string;
  /** Texto libre: alergias declaradas. */
  allergies?: string;
  /** Texto libre: medicamentos de uso permanente. */
  medications?: string;
  /** True si el participante declara explícitamente NO tener condiciones médicas relevantes. */
  noMedicalConditions?: boolean;
  /** Lugar (municipio) donde se expidió el documento de identidad — usado en consentimiento de mayor. */
  idExpeditionCity?: string;

  // ===== FOTO Y DOCUMENTOS =====
  photoUrl?: string;
  photoPath?: string;

  /** Foto ANVERSO del documento de identidad */
  documentFrontUrl?: string;
  documentFrontPath?: string;
  /** Metadata de encriptación del anverso */
  documentFrontEncryption?: { key: string; alg: string };
  /** Content-type original del anverso */
  documentFrontContentType?: string;

  /** Foto REVERSO del documento de identidad (documentos tipo tarjeta: CC, TI, RC) */
  documentBackUrl?: string;
  documentBackPath?: string;
  /** Metadata de encriptación del reverso */
  documentBackEncryption?: { key: string; alg: string };
  /** Content-type original del reverso */
  documentBackContentType?: string;

  /** Soporte de EPS/Seguro de salud */
  epsDocumentUrl?: string;
  epsDocumentPath?: string;
  /** Metadata de encriptación del documento EPS */
  epsDocumentEncryption?: { key: string; alg: string };
  /** Content-type original del documento EPS */
  epsDocumentContentType?: string;

  // ===== VINCULACIÓN CON CUENTA =====
  linkedUserId?: string; // ID del usuario si tiene cuenta
  linkedAt?: Timestamp;

  // ===== ACUDIENTE (para menores u otras personas a cargo de un usuario) =====
  /**
   * userId del acudiente (padre/madre/tutor) que administra a esta persona desde su cuenta.
   * Solo se usa cuando esta Person NO tiene cuenta propia (`linkedUserId` está vacío)
   * y depende de otro usuario para ser gestionada y notificada.
   */
  guardianUserId?: string;
  /** Relación del acudiente con la persona (informativo) */
  guardianRelation?:
    | "padre"
    | "madre"
    | "tutor"
    | "abuelo"
    | "abuela"
    | "hermano"
    | "hermana"
    | "otro";
  /** Nombre denormalizado del acudiente para mostrar sin lookup */
  guardianName?: string;
  /**
   * Email original que tenía la persona antes de migrar a guardian model.
   * Se conserva por trazabilidad. NO se usa para notificaciones ni para login.
   */
  _legacyEmail?: string;

  // ===== UBICACIÓN ORGANIZACIONAL =====
  // Donde fue registrada originalmente la persona
  churchId?: string;
  associationId?: string;

  // ===== ESTADO =====
  status: "active" | "inactive";
}

// ============================================
// MEMBER (Membresía en un club específico)
// ============================================

/**
 * Membresía de una persona en un club
 * Vincula una Person con un Club en un período específico
 *
 * NOTA: Los datos personales están en Person, no aquí.
 * Member solo contiene datos de la membresía.
 */
export interface Member extends AuditFields {
  id: string;

  // ===== VINCULACIÓN =====
  personId: string; // → Person.id (REQUERIDO)
  clubId: string;
  periodId?: string;
  unitId?: string;

  // ===== CONTEXTO ORGANIZACIONAL =====
  /** @deprecated opcional — se obtiene del club */
  churchId?: string;
  associationId?: string;
  /** Sección del miembro, derivada de su edad */
  section?: ClubType;
  /**
   * Sección donde la persona EJERCE su rol staff (no necesariamente la misma
   * que su `section` etaria).
   *
   * Solo aplica cuando `role` es un rol de staff (director, subdirector,
   * consejero, instructor, tesorero, secretario). Para `role='member'` debe
   * estar vacío.
   *
   * Ejemplo: Diego tiene `section='guias_mayores'` (por edad) y
   * `staffSection='conquistadores'` (donde sirve como director).
   *
   * Si el member es staff y `staffSection` está vacío, se asume
   * `staffSection === section` (compat con datos históricos).
   */
  staffSection?: ClubType;
  /** @deprecated Usar section */
  clubType?: ClubType;
  // ===== DATOS DE MEMBRESÍA =====
  role: MemberRole;
  roleHistory?: MemberRoleHistory[];
  roleStartDate?: Timestamp;
  clase?: MemberClass; // Clase/Nivel (Amigo, Compañero, etc.)
  position?: string;
  positionId?: string;

  // ===== ESTADO =====
  status: MemberStatus;
  statusReason?: string;

  // ===== REGISTRO =====
  registrationDate: Timestamp;
  registrationPaid: boolean;
  registrationPaymentDate?: Timestamp;

  // ===== DATOS DESNORMALIZADOS (para listas rápidas) =====
  personName?: string; // Copia de Person.fullName para visualización
  personPhoto?: string; // Copia de Person.photoUrl para visualización

  // ===== F11: TRASLADOS Y CLUBES FUSIÓN =====
  /**
   * Club del que vino este miembro. Se escribe al trasladarlo de club y al
   * agregarlo a un club fusión (membresía aditiva). Es lo que permite
   * "disolver" una fusión devolviendo a cada quien a su club de origen —
   * la ausencia de este campo fue lo que dejó 21 fichas atrapadas cuando
   * se disolvió FUSIÓN COAL GEDEÓN a mano (2026-08-05).
   */
  originClubId?: string;
  /** Cuándo y quién hizo el último traslado. */
  transferredAt?: Timestamp;
  transferredBy?: string;
}

/**
 * Member con datos de Person incluidos (para vistas)
 * Usado cuando necesitamos mostrar datos personales junto con la membresía
 */
export interface MemberWithPerson extends Member {
  person: Person;
}

// ===== F14: SOLICITUDES DE TRASLADO ENTRE CLUBES =====

export type MemberTransferRequestStatus =
  | "pending"
  | "completed"
  | "rejected"
  | "cancelled";

/**
 * Solicitud de traslado de un miembro entre dos clubes, SIN pasar por el
 * admin: el club destino la pide, el club de origen la aprueba y esa
 * aprobación ejecuta el traslado.
 *
 * Origen (2026-08-10): una guía mayor que fue al campamento con MUTIS quería
 * ir esta vez con ÁGAPE, y el único camino era que el admin la trasladara a
 * mano. Peor: el camino "sucio" sí existía — el director destino podía
 * agregarla por documento y dejarla con DOS membresías activas, sobreescribiendo
 * de paso su ficha. Esta solicitud reemplaza ese hueco.
 *
 * El ID del doc es determinístico — `${targetClubId}__${personId}` — igual que
 * el del member que se crearía al aprobar. Eso da: (a) una sola solicitud
 * pendiente por persona+destino, y (b) que las rules puedan validar la
 * aprobación con un simple get() del mismo ID del member que se está creando.
 *
 * Lleva el TELÉFONO de ambos directores porque el traslado casi siempre
 * necesita una llamada antes del clic.
 */
export interface MemberTransferRequest {
  id: string;
  personId: string;
  personName: string;
  /** Documento enmascarado para mostrar sin exponer la ficha: "CC •••8912". */
  personDocMasked: string;

  originClubId: string;
  originClubName: string;
  /** members/{originClubId}__{personId}: la membresía que se moverá. */
  originMemberId: string;
  /**
   * Cargo interno en el club de origen (instructor, consejero…), si lo tiene.
   * Los cargos NO viajan: al aprobar llega como "member" y el club nuevo
   * asigna el suyo. Una DIRECCIÓN (roleAssignments) sí bloquea el traslado.
   */
  originRole?: string;
  /** Contacto del director de origen (para que el destino coordine). */
  originDirectorName?: string;
  originDirectorPhone?: string;

  targetClubId: string;
  targetClubName: string;

  /** Quién pide (director del club destino) y su contacto. */
  requestedBy: string;
  requestedByName: string;
  requestedByPhone?: string;
  requestedByEmail?: string;
  /** Mensaje corto del solicitante ("la familia se mudó a Floridablanca"). */
  note?: string;

  /**
   * La persona está A CARGO de un acudiente (persons.guardianUserId): el
   * origen debe confirmar el traslado con la familia antes de aprobar. El
   * vínculo con el acudiente NO se toca al trasladar — la ficha viaja entera.
   */
  personHasGuardian?: boolean;
  /** true si la ficha tiene cuenta propia (persons.linkedUserId). */
  personHasOwnAccount?: boolean;

  status: MemberTransferRequestStatus;
  decidedBy?: string;
  decidedAt?: Timestamp;
  decisionNote?: string;

  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * Tipo legacy para compatibilidad con código existente
 * @deprecated Usar Member + Person separados
 *
 * Este tipo añade campos de Person directamente en Member
 * para facilitar la migración gradual del código existente.
 * El campo `person` permite acceder al objeto Person completo
 * desde el nuevo código sin romper el código existente.
 */
export interface MemberLegacy extends Member {
  // Objeto Person completo (nueva arquitectura — opcional para compatibilidad)
  person?: Person;
  // Datos personales (legacy - vienen de Person, mantenidos por compatibilidad)
  firstName?: string;
  lastName?: string;
  fullName?: string;
  memberType?: MemberType;
  idDocumentType?: IdDocumentType;
  idDocumentNumber?: string;
  bloodType?: BloodType;
  birthDate?: Timestamp;
  gender?: Gender;
  phone?: string;
  email?: string;
  healthInsurance?: string;
  photoUrl?: string;
  photoPath?: string;
  documentFrontUrl?: string;
  documentFrontPath?: string;
  epsDocumentUrl?: string;
  epsDocumentPath?: string;
  address?: string;
  emergencyContact?: EmergencyContact;
  linkedUserId?: string;
  linkedAt?: Timestamp;
}

// ============================================
// MULTI-CARGO (MemberRoleAssignment)
// ============================================
//
// Permite que una misma persona ejerza N cargos simultáneamente en clubes y
// secciones distintas dentro de la misma organización, sin tocar el campo
// legacy `member.role` / `member.staffSection` (esos siguen existiendo por
// compatibilidad y quedan sincronizados con la asignación "principal").
//
// Colección: /memberRoles
// Doc id: autogenerado
// Índices requeridos (Firestore):
//   - (personId, status)
//   - (clubId, section, status)
//   - (clubId, role, status)
//   - (memberId, status)
//
// El activador de la funcionalidad completa vive en:
//   /appConfig/features.memberRolesEnabled (boolean)

/** Estado de una asignación de cargo dentro de un club/sección */
export type MemberRoleAssignmentStatus = "active" | "inactive";

/**
 * Asignación granular de un cargo a una persona en un club y sección.
 *
 * Reglas de negocio:
 * - Una persona puede tener múltiples asignaciones activas simultáneas siempre
 *   que difieran en `(clubId, section, role)`.
 * - `personId` es la fuente de verdad. `memberId` se guarda para acelerar
 *   queries y validar consistencia (el member debe pertenecer al mismo club).
 * - Cambiar de cargo NO borra el registro anterior: se marca `status="inactive"`
 *   y se emite otro documento con el nuevo cargo. Esto preserva historial.
 * - Cuando `role === "member"` este documento no debe crearse — la membresía
 *   base ya lo representa via `Member`.
 */
export interface MemberRoleAssignment extends AuditFields {
  id: string;
  memberId: string;
  personId: string;
  clubId: string;
  /** Sección donde se ejerce el cargo. Requerida para roles de staff. */
  section: ClubType;
  role: MemberRole;
  status: MemberRoleAssignmentStatus;
  /** Inicio del cargo (obligatorio) */
  startDate: Timestamp;
  /** Fin del cargo (sólo si status='inactive') */
  endDate?: Timestamp;
  /** uid del usuario que asignó el cargo */
  assignedBy: string;
  /** Nombre desnormalizado del asignador para auditoría rápida */
  assignedByName?: string;
  /** Motivo del cambio (opcional) */
  notes?: string;
  /**
   * Copia desnormalizada del nombre de la persona para listados y auditoría
   * — evita joins en las UI de admin y filtros por texto.
   */
  personName?: string;
}

// ============================================
// RBAC DINÁMICO (Permisos + Políticas de rol)
// ============================================
//
// El modelo estático (permisos codificados por rol) se reemplaza por dos
// colecciones editables:
//
//   /permissionDefinitions  — catálogo de permisos posibles (source of truth)
//   /rolePolicies           — mapeo rol → lista de permisos
//
// Un usuario obtiene el conjunto de permisos como la UNIÓN de:
//   - Política global del `role` de cada RoleAssignment/MemberRoleAssignment
//     que tenga activa
//   - Política scope='club' que apunta al mismo `clubId` (override específico)
//
// Convención de IDs:
//   permissionDefinitions/{module}.{action}   (ej: finance.write)
//   rolePolicies/{role}                       (política global por rol)
//   rolePolicies/{role}__{clubId}             (override por club)

/** Acción concreta permitida por un permiso */
export type PermissionAction =
  | "read"
  | "write"
  | "delete"
  | "approve"
  | "export"
  | "admin";

/**
 * Definición de un permiso registrado en el sistema.
 *
 * `defaultRoles` se usa SOLO como semilla al crear la política inicial; a
 * partir de allí la fuente de verdad es `/rolePolicies`.
 */
export interface PermissionDefinition extends AuditFields {
  id: string; // "{module}.{action}"
  module: string; // ej: "finance", "members", "events", "reports"
  action: PermissionAction;
  description: string;
  /** Roles que reciben este permiso por defecto en el seed */
  defaultRoles: MemberRole[];
  /** Si es false, el permiso queda oculto en la UI de admin (obsoleto) */
  enabled?: boolean;
}

export type RolePolicyScope = "global" | "club";

/**
 * Política de permisos para un rol.
 *
 * - scope='global': aplica a cualquier `clubId` cuando el usuario tenga ese
 *   rol asignado.
 * - scope='club': override para un club específico. El id del doc lleva el
 *   sufijo `__{clubId}` para permitir lecturas directas.
 *
 * `permissions` contiene los ids del catálogo `/permissionDefinitions`.
 */
export interface RolePermissionPolicy extends AuditFields {
  id: string;
  role: MemberRole;
  scope: RolePolicyScope;
  /** Requerido cuando scope='club' */
  clubId?: string;
  permissions: string[];
  /** Texto libre para justificar el override (auditoría) */
  notes?: string;
}

// ============================================
// PROGRESS
// ============================================

// Tipos de logro simplificados (honor = specialty, son lo mismo)
export type AchievementType =
  | "specialty" // Especialidades/Honores
  | "mastery" // Maestrías
  | "certification" // Certificaciones, barras, medallones
  | "class" // Completar clase progresiva
  | "investiture" // Investidura
  | "position"; // Cargo/Posición

export interface Evidence {
  type: "image" | "document" | "link";
  url: string;
  description?: string;
}

export interface Achievement extends AuditFields {
  id: string;
  personId: string;
  memberId: string;
  clubId: string;
  periodId: string;
  type: AchievementType;
  catalogId: string;
  catalogName: string;
  earnedDate: Timestamp;
  instructorId?: string;
  instructorName?: string;
  evidences?: Evidence[];
  certificateIssued: boolean;
  certificateIssuedAt?: Timestamp;
  certificateUrl?: string;
  status: "valid" | "revoked";
}

export interface ClassRequirementProgress {
  requirementId: string;
  name: string;
  completed: boolean;
  completedAt?: Timestamp;
  completedBy?: string;
  notes?: string;
}

/**
 * Requisito configurado por un club para una clase específica
 */
export interface ClubClassRequirement {
  id: string;
  name: string;
  description?: string;
  order: number;
}

/**
 * Configuración de requisitos de una clase para un club específico
 * Permite que cada club defina sus propios requisitos
 */
export interface ClubClassConfig extends AuditFields {
  id: string;
  clubId: string;
  classId: string;
  className: string;
  requirements: ClubClassRequirement[];
}

// Estado del progreso de clase
export type ClassProgressStatus =
  | "in_progress" // En progreso - completando requisitos
  | "completed" // Completada - todos los requisitos cumplidos
  | "pending_approval" // Pendiente de aprobación por la asociación
  | "approved" // Aprobada por la asociación
  | "rejected" // Rechazada por la asociación
  | "invested"; // Investido oficialmente

export interface ClassProgress extends AuditFields {
  id: string;
  personId: string;
  memberId: string;
  memberName?: string; // Nombre del miembro (desnormalizado para visualización)
  clubId: string;
  periodId: string;
  classId: string;
  className: string;
  clubType: ClubType;
  status: ClassProgressStatus;
  startDate: Timestamp;
  completedDate?: Timestamp;
  investedDate?: Timestamp;
  requirements: ClassRequirementProgress[];
  completionPercentage: number;

  // Flujo de aprobación
  submittedForApprovalAt?: Timestamp;
  submittedBy?: string; // userId que envió a aprobación
  approvedAt?: Timestamp;
  approvedBy?: string; // userId del director que aprobó
  rejectedAt?: Timestamp;
  rejectedBy?: string;
  rejectionReason?: string;
}

// ============================================
// ATTENDANCE
// ============================================

export type MeetingType =
  | "regular"
  | "special"
  | "training"
  | "service"
  | "other";

export interface Meeting extends AuditFields {
  id: string;
  clubId: string;
  periodId: string;
  type: MeetingType;
  title: string;
  description?: string;
  date: Timestamp;
  startTime?: string; // "14:00"
  endTime?: string; // "17:00"
  location?: string;
  isClosed: boolean;
  attendanceCount: number;
  totalMembers: number;
}

export type AttendanceType = "meeting" | "event";
export type AttendanceStatus = "present" | "absent" | "late" | "excused";

export interface Attendance extends AuditFields {
  id: string;
  type: AttendanceType;
  meetingId?: string;
  clubId: string;
  eventId?: string;
  date: Timestamp;
  memberId: string;
  personId: string;
  personName: string;
  status: AttendanceStatus;
  justification?: string;
  checkedInAt?: Timestamp;
  checkedInBy?: string;
}

// ============================================
// FINANCE
// ============================================

export type ChargeStatus =
  | "pending"
  | "partial"
  | "paid"
  | "cancelled"
  | "overdue";

export interface Charge extends AuditFields {
  id: string;
  clubId: string;
  periodId: string;
  memberId: string;
  personId: string;
  personName: string;
  conceptType: string;
  conceptName: string;
  description?: string;
  eventId?: string;
  eventName?: string;
  amount: number;
  currency: string;
  dueDate?: Timestamp;
  status: ChargeStatus;
  amountPaid: number;
  balance: number;
}

export type PaymentMethod =
  | "efectivo"
  | "transferencia"
  | "nequi"
  | "daviplata"
  | "otro";

export interface Payment extends AuditFields {
  id: string;
  chargeId: string;
  clubId: string;
  periodId: string;
  memberId: string;
  personId: string;
  personName: string;
  amount: number;
  currency: string;
  paymentMethod: PaymentMethod;
  reference?: string;
  paymentDate: Timestamp;
  status: "valid" | "reversed";
  reversedAt?: Timestamp;
  reversedBy?: string;
  reversalReason?: string;
  receivedBy: string;
  receivedByName: string;
}

export interface Expense extends AuditFields {
  id: string;
  clubId: string;
  periodId: string;
  category: string;
  description: string;
  eventId?: string;
  eventName?: string;
  amount: number;
  currency: string;
  vendor?: string;
  expenseDate: Timestamp;
  receiptUrl?: string;
  receiptNumber?: string;
  status: "valid" | "cancelled";
  registeredBy: string;
  registeredByName: string;
}

// ============================================
// EVENTS
// ============================================

export type EventStatus =
  | "draft"
  | "published"
  | "registration_closed"
  | "in_progress"
  | "completed"
  | "cancelled";
export type EventLevel =
  | "union"
  | "association"
  | "district"
  | "church"
  | "club";
export type EventType =
  | "camporee"
  | "campamento"
  | "convocatoria"
  | "entrenamiento"
  | "investidura"
  | "social"
  | "servicio"
  | "otro";

export interface EventRegistrationConfig {
  startDate: Timestamp;
  endDate: Timestamp;
  paymentDeadline?: Timestamp;
  maxCapacity?: number;
  allowWaitlist: boolean;
  requiresApproval: boolean;
}

export interface EventPricing {
  category: string;
  label: string;
  amount: number;
  earlyBirdAmount?: number;
  earlyBirdDeadline?: Timestamp;
  /**
   * Precio aplicable cuando el club inscrito pertenece a un distrito incluido
   * en `Event.discountedDistrictIds`. Opcional: si no se define, el descuento
   * por distrito no aplica a esta categoría (se usa `amount`).
   */
  discountedAmount?: number;
  /** Variante early-bird del precio con descuento (si aplica). */
  discountedEarlyBirdAmount?: number;
}

export interface Event extends AuditFields {
  id: string;
  name: string;
  description: string;
  eventType: EventType;
  level: EventLevel;
  organizerId: string; // ID del club, iglesia, distrito, etc. que organiza
  organizerName: string;

  // Fechas
  startDate: Timestamp;
  endDate: Timestamp;

  // Ubicación
  location: {
    name: string;
    address: string;
    city: string;
    department?: string;
    coordinates?: { lat: number; lng: number };
  };

  // Configuración
  /**
   * Clubes a los que se les REABRE la inscripción aunque la ventana del evento
   * ya haya cerrado.
   *
   * La ventana (`registrationConfig.startDate/endDate`) es del EVENTO: moverla
   * para dejar entrar a un club tardío se la abre a los 47, y en un evento con
   * premio a los 10 primeros eso es repartir de nuevo lo ya repartido. Esta
   * lista deja entrar a uno solo.
   *
   * La escribe la Asociación (script `abrir-inscripciones-club.mjs`), nunca un
   * director: en las rules vive junto a la ventana, así que un club que se
   * añadiera a sí mismo se auto-reabriría las inscripciones.
   *
   * Hay que QUITAR al club cuando termine de inscribir: mientras esté en la
   * lista sigue abierto, y nada lo cierra solo.
   */
  registrationOpenClubIds?: string[];
  clubTypesAllowed: ClubType[];
  /**
   * F13 — Excepción de edad de transición ("temporales").
   *
   * Caso real (campamento de Aventureros 2026): los niños que cumplen 10 años
   * durante el primer semestre pasan automáticamente a Conquistadores por
   * edad, pero nunca alcanzaron a ir al campamento de Aventureros. La
   * Asociación decidió darles esa última oportunidad — sin cambiarlos de
   * sección (siguen en Conquistadores para clases, asistencia y reportes):
   * la excepción vale SOLO para inscribirse a este evento.
   *
   * Quedan habilitados los miembros que:
   *   · pertenezcan a una de las `fromSections`,
   *   · hayan nacido entre `bornFrom` y `bornTo` (ISO yyyy-mm-dd), y
   *   · NO estén inscritos en `excludeIfRegisteredInEventId` (el campamento
   *     de su propia sección; si ya fueron, no repiten etapa).
   */
  transitionalAgeException?: {
    enabled: boolean;
    fromSections: ClubType[];
    bornFrom: string;
    bornTo: string;
    excludeIfRegisteredInEventId?: string;
    /** Texto de la etiqueta que ve el director. */
    label?: string;
  };
  registrationConfig: EventRegistrationConfig;
  pricing: EventPricing[];

  /**
   * IDs de distritos cuyos clubes obtienen el precio `discountedAmount` de cada
   * categoría. Se resuelve a través de `Club.churchId → Church.districtId`.
   * Vacío o undefined ⇒ ningún descuento por distrito.
   */
  discountedDistrictIds?: string[];
  /** Etiqueta visible para el descuento (ej. "Zona rural"). Solo UI. */
  discountLabel?: string;

  // Estado
  status: EventStatus;

  // Imágenes
  coverImageUrl?: string;

  // Contadores (desnormalizados para rendimiento)
  registeredCount: number;
  confirmedCount: number;

  // Contacto
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;

  // Agenda del evento (opcional, editable por el organizador)
  agenda?: EventAgenda;

  /**
   * Configuración por evento de qué ítems del check-in de documentos son
   * obligatorios para los inscritos. Si no se define, el comportamiento por
   * defecto es que TODOS los ítems son obligatorios (retro-compat).
   * Los ítems no listados heredan el default (required=true).
   */
  consentChecklistConfig?: EventConsentChecklistConfig;

  /**
   * Fecha límite para que los clubes completen los documentos del evento
   * (adjuntos por club como contrato de transporte, carta de junta, etc.).
   * Solo informativa por ahora — se muestra en la UI cerca de los documentos.
   */
  documentsDeadline?: Timestamp;
}

/**
 * IDs de los ítems del checklist de consentimiento por evento.
 * Debe coincidir con `ConsentChecklistItem.id` en la definición del checklist.
 */
export type ConsentChecklistItemId =
  | "idDocumentFront"
  | "idDocumentBack"
  | "epsCertificate"
  | "medicalAndTravel"
  | "dataConsent"
  | "participationConsent"
  | "participantPhoto";

/**
 * Config de qué ítems del check-in son obligatorios por evento.
 * `true` (o ausencia) = obligatorio; `false` = opcional (no cuenta para
 * bloquear la finalización del check-in).
 */
export type EventConsentChecklistConfig = Partial<
  Record<ConsentChecklistItemId, boolean>
>;

/** Bloque dentro de un día de la agenda. */
export interface EventAgendaBlock {
  /** ID estable client-side (uuid o `${dayId}_${idx}`) para keys de React. */
  id: string;
  /** Hora inicio "HH:MM" (24h). */
  startTime: string;
  /** Hora fin "HH:MM" (24h). */
  endTime: string;
  /** Título de la actividad. */
  title: string;
  /** Descripción / detalle opcional. */
  description?: string;
  /** Lugar opcional dentro del campamento. */
  location?: string;
}

/** Día de la agenda con su coordinador y bloques. */
export interface EventAgendaDay {
  id: string;
  /** Fecha "YYYY-MM-DD" (sin hora). */
  date: string;
  /** Coordinador(es) del día — texto libre. */
  dayCoordinator?: string;
  blocks: EventAgendaBlock[];
}

/** Agenda completa del evento (embebida en el doc del evento). */
export interface EventAgenda {
  days: EventAgendaDay[];
  /** Se incrementa en cada guardado. Permite detectar "agenda actualizada" en cliente. */
  version: number;
  updatedAt?: Timestamp;
  updatedBy?: string;
  updatedByName?: string;
}

export type RegistrationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "cancelled"
  | "waitlist";
export type PaymentStatus = "pending" | "partial" | "paid" | "refunded";

export interface EventRegistration extends AuditFields {
  id: string;
  eventId: string;
  eventName: string;

  // Quien inscribe
  clubId: string;
  clubName: string;
  registeredById: string;
  registeredByName: string;

  // Persona inscrita
  personId: string;
  personName: string;
  memberId?: string;

  // Categoría y precio
  category: "pathfinder" | "staff" | "guest" | "family";
  unitPrice: number;

  /**
   * Planilla oficial de inscripción: ¿asiste CON uniforme?
   *
   * La planilla de la Asociación separa a los aventureros uniformados de los
   * "amigos sin uniforme", y eso no es un dato de la ficha del miembro sino
   * del campamento al que va: el mismo niño puede ir uniformado este año y
   * de visita el siguiente. Por eso vive en la inscripción y lo marca el
   * director desde la tabla "Mis Inscritos".
   *
   * Ausente = sin marcar, y la planilla lo manda a los "amigos sin uniforme".
   */
  hasUniform?: boolean;

  // Estado
  status: RegistrationStatus;
  paymentStatus: PaymentStatus;
  amountPaid: number;

  // Fechas
  registrationDate: Timestamp;
  approvalDate?: Timestamp;
  approvedBy?: string;

  // Rechazo
  rejectedAt?: Timestamp;
  rejectedBy?: string;
  rejectionReason?: string;

  // Intercambio de integrante (swap). Trazabilidad bidireccional.
  /** personId del integrante que ENTRÓ al cancelar esta inscripción por swap. */
  swappedOutTo?: string;
  /** personId del integrante que SALIÓ; esta inscripción heredó su precio/pago. */
  swappedInFrom?: string;
  swappedAt?: Timestamp;

  // Comprobante de pago
  receiptUrl?: string;
  receiptPath?: string;
  receiptUploadedAt?: Timestamp;
  /** Metadata de encriptación del recibo */
  receiptEncryption?: { key: string; alg: string };
  /** Content-type original del recibo */
  receiptContentType?: string;

  // Notas
  notes?: string;
  medicalNotes?: string;
  dietaryRestrictions?: string;
}

// Ranking de inscripción de clubes a un evento
export interface ClubEventRanking extends AuditFields {
  id: string;
  eventId: string;
  clubId: string;
  clubName: string;
  totalRegistered: number;
  totalPayments: number; // cantidad de pagos subidos por el club
  totalPaymentAmount: number; // monto total de pagos subidos
  completedAt?: Timestamp; // cuando alcanzó el mínimo (10 inscritos + 1 pago)
  position?: number; // 1, 2, 3... asignado al completar
  /** @deprecated usar totalPayments */
  totalWithReceipt?: number;
}

// Pago de un club para un evento (N pagos por club)
export type EventPaymentStatus = "pending" | "approved" | "rejected";

export interface EventPayment extends AuditFields {
  id: string;
  eventId: string;
  eventName: string;
  clubId: string;
  clubName: string;

  // Monto
  amount: number;
  description?: string;
  /**
   * Monto ORIGINAL declarado por el club al subir el pago.
   * Se llena automáticamente cuando un admin ajusta el monto durante la
   * validación (auditoría de quién corrigió y desde qué valor).
   */
  originalAmount?: number;
  amountAdjustedBy?: string;
  amountAdjustedByName?: string;
  amountAdjustedAt?: Timestamp;

  // Comprobante
  receiptUrl: string;
  receiptPath: string;
  receiptContentType: string;
  receiptEncryption?: { key: string; alg: string };

  // Estado de validación (por asociación)
  status: EventPaymentStatus;
  validatedBy?: string;
  validatedByName?: string;
  validatedAt?: Timestamp;
  rejectionReason?: string;

  // Quien subió
  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: Timestamp;

  /**
   * Método de pago. "receipt" (default) requiere comprobante en Storage.
   * "cash" lo registra un admin directamente (efectivo recibido en el evento),
   * sin comprobante adjunto, y queda como `approved` automáticamente.
   */
  paymentMethod?: "receipt" | "cash";

  notes?: string;
}

// ============================================
// EVENT CLUB ATTACHMENTS (documentos anexos)
// ============================================
// Cada documento se sube por un club para un evento (contrato de transporte,
// voto de junta, etc.). Visibles para el club dueño y para el organizador.

export type EventClubAttachmentTag =
  | "contrato_transporte"
  | "contrato_camion"
  | "voto_junta"
  | "permiso_padres"
  | "seguro"
  | "formato_economas"
  | "carta_junta_iglesia"
  | "otro";

export interface EventClubAttachment extends AuditFields {
  id: string;
  eventId: string;
  eventName: string;
  clubId: string;
  clubName: string;

  tag: EventClubAttachmentTag;
  /** Etiqueta personalizada cuando tag === "otro" */
  customLabel?: string;
  /** Descripción / notas opcionales */
  description?: string;

  fileName: string;
  fileUrl: string;
  filePath: string;
  fileContentType: string;
  fileSize: number;

  uploadedBy: string;
  uploadedByName: string;
  uploadedAt: Timestamp;
}

// ============================================
// EVENT REGISTRATION AUDIT (admin actions)
// ============================================

export type RegistrationAuditAction =
  | "edit_fields"
  | "transfer_club"
  | "force_status"
  | "delete"
  | "reactivate"
  | "swap";

export interface RegistrationAuditChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface EventRegistrationAudit extends AuditFields {
  id: string;
  registrationId: string;
  eventId: string;
  eventName?: string;
  clubId: string;
  clubName?: string;
  personId: string;
  personName?: string;

  action: RegistrationAuditAction;
  changes: RegistrationAuditChange[];
  /** Razón opcional capturada en el modal */
  reason?: string;

  performedBy: string;
  performedByName?: string;
  performedAt: Timestamp;
}

// ============================================
// SECURITY
// ============================================

export type Module =
  | "ORG"
  | "MIEMBROS"
  | "PROGRESO"
  | "EVENTOS"
  | "ASISTENCIA"
  | "FINANZAS"
  | "DOCUMENTOS"
  | "REPORTES"
  | "CATALOGOS";

export type Action =
  | "view"
  | "create"
  | "update"
  | "delete"
  | "approve"
  | "export"
  | "manage"
  | "viewSensitive";

export type ScopeTypeOld =
  | "union"
  | "association"
  | "district"
  | "church"
  | "club"
  | "event";

export interface Permission {
  module: Module;
  actions: Action[];
}

export interface Role extends AuditFields {
  id: string;
  name: string;
  description?: string;
  permissions: Permission[];
  isSystem: boolean;
  applicableScopes: ScopeTypeOld[];
}

export interface RoleAssignmentOld extends AuditFields {
  id: string;
  userId: string;
  roleId: string;
  scopeType: ScopeTypeOld;
  scopeId: string;
  roleName: string;
  scopeName: string;
  active: boolean;
  startAt: Timestamp;
  endAt?: Timestamp;
}

// ============================================
// CATALOGS
// ============================================

export interface Specialty extends AuditFields {
  id: string;
  name: string;
  code?: string;
  description?: string;
  category: string;
  categorySlug?: string;
  clubTypes: ClubType[];
  requirements?: string[];
  badgeUrl?: string;
  imageUrl?: string;
  level?: "basico" | "avanzado" | "master";
  isAdvanced?: boolean;
  status: Status;
}

export interface Maestria extends AuditFields {
  id: string;
  name: string;
  slug: string;
  description?: string;
  localImage: string;
  requiredSpecialties?: string[];
  clubTypes: ClubType[];
  status: Status;
}

/**
 * Tipo de certificación (certificaciones, barras, medallas)
 */
export type CertificationType =
  | "certificacion"
  | "barra"
  | "medallon"
  | "estrella"
  | "diamante";

export interface Certificacion extends AuditFields {
  id: string;
  name: string;
  slug: string;
  description?: string;
  localImage: string;
  type: CertificationType;
  clubTypes: ClubType[];
  status: Status;
}

export interface ClassCatalog extends AuditFields {
  id: string;
  name: string;
  code?: string;
  clubType: ClubType;
  order: number;
  minAge?: number;
  maxAge?: number;
  description?: string;

  // Para clases con versión avanzada (Amigo → Amigo Avanzado)
  isAdvanced?: boolean;
  baseClassId?: string; // ID de la clase base si es avanzada

  // Para Guías Mayores: Guía Mayor, GM Avanzado, GM Instructor
  gmLevel?: "guia_mayor" | "gm_avanzado" | "gm_instructor";

  requirements?: {
    id: string;
    section: string;
    description: string;
    type: "check" | "count" | "text";
    targetValue?: number;
  }[];
  status: Status;
}

// Constantes de clases por tipo de club
export const ADVENTURER_CLASSES = [
  "Corderitos",
  "Abejitas",
  "Constructores",
  "Castorcitos",
  "Aves Madrugadoras",
  "Rayitos de Sol",
  "Manos Ayudadoras",
] as const;

export const PATHFINDER_CLASSES = [
  "Amigo",
  "Compañero",
  "Explorador",
  "Orientador",
  "Viajero",
  "Guía",
] as const;

export const MASTER_GUIDE_CLASSES = [
  "Guía Mayor",
  "Guía Mayor Avanzado",
  "Guía Mayor Instructor",
] as const;

export type AdventurerClass = (typeof ADVENTURER_CLASSES)[number];
export type PathfinderClass = (typeof PATHFINDER_CLASSES)[number];
export type MasterGuideClass = (typeof MASTER_GUIDE_CLASSES)[number];

// Alias para retrocompatibilidad
export type ProgressClass = ClassCatalog;

export interface Position extends AuditFields {
  id: string;
  name: string;
  code?: string;
  description?: string;
  level: "club" | "unit" | "church" | "district" | "association" | "union";
  clubTypes?: ClubType[];
  permissions?: string[];
  status: Status;
}

export interface ConceptType extends AuditFields {
  id: string;
  name: string;
  description?: string;
  category: "income" | "expense";
  status: Status;
}

// ============================================
// EVENT CONSENT (Check-in de documentos por evento)
// ============================================

/**
 * Tipo de consentimiento informado según edad del participante
 * en la fecha de FINALIZACIÓN del evento.
 */
export type ConsentParticipantType = "minor" | "adult";

export type ConsentStatus = "pending" | "in_progress" | "completed";

/**
 * Documento subido como soporte (ID, EPS) — referencia a Storage encriptado.
 */
export interface ConsentDocument {
  url: string;
  path: string;
  contentType: string;
  encryption?: { key: string; alg: string };
  uploadedAt: Timestamp;
  fileName?: string;
}

/**
 * Firma digital capturada en canvas (PNG dataURL subido a Storage).
 */
export interface ConsentSignature {
  url: string;
  path: string;
  signedAt: Timestamp;
}

/**
 * Datos del padre/madre/acudiente que firma el consentimiento de un menor.
 */
export interface ConsentParentRecord {
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  address: string;
  relationship: "padre" | "madre" | "acudiente";
  signature: ConsentSignature;
  /** Foto del padre tomada en vivo o subida — para verificar veracidad */
  verificationPhoto: ConsentDocument;
}

/**
 * Snapshot de los datos auto-rellenados al momento de firmar el consentimiento.
 * Se guarda copia para inmutabilidad: aunque cambien los datos del miembro
 * o del evento después, el PDF generado refleja lo firmado.
 */
export interface ConsentFormSnapshot {
  /** Datos del miembro */
  memberFullName: string;
  memberIdDocumentType: IdDocumentType;
  memberIdDocumentNumber: string;
  memberBirthDate: string; // ISO date
  memberAddress?: string;
  memberHealthInsurance?: string; // EPS
  memberMedicalConditions?: string;
  memberAllergies?: string;
  memberMedications?: string;
  /** True si declaró explícitamente que no tiene condiciones médicas relevantes. */
  memberNoMedicalConditions?: boolean;
  /** Lugar de expedición del documento de identidad (solo mayor). */
  memberIdExpeditionCity?: string;
  /** Datos del evento */
  eventName: string;
  eventStartDate: string; // ISO
  eventEndDate: string; // ISO
  eventLocationName: string;
  eventLocationCity: string;
  eventLocationAddress?: string;
  /** Datos del club / iglesia / distrito */
  clubName: string;
  churchName?: string;
  districtName?: string;
  associationName: string;
  /** Voto de junta (lo escribe el director / asociación al crear evento) */
  boardVoteNumber?: string;
  boardVoteDate?: string; // ISO
  /** Ciudad de salida y regreso (los completa el participante / padre) */
  departureCity?: string;
  returnCity?: string;
}

/**
 * Consentimiento informado de un participante para un evento específico.
 * Documento en colección raíz `eventConsents` con id = `${eventId}_${memberId}`.
 */
export interface EventConsent extends AuditFields {
  id: string;
  eventId: string;
  eventName: string;
  memberId: string;
  personId: string;
  registrationId: string;
  clubId: string;
  /** Asociación scope para reglas */
  associationId: string;

  participantType: ConsentParticipantType;
  status: ConsentStatus;

  /** Snapshot inmutable de los datos al momento de firmar */
  formSnapshot: ConsentFormSnapshot;

  /** Documentos de soporte */
  /** @deprecated Usar idDocumentFront / idDocumentBack. Mantener para compat. */
  idDocument?: ConsentDocument;
  /** Anverso del documento de identidad */
  idDocumentFront?: ConsentDocument;
  /** Reverso del documento de identidad */
  idDocumentBack?: ConsentDocument;
  epsCertificate?: ConsentDocument;

  /** Aceptación de tratamiento de datos (Ley 1581 de 2012) */
  dataConsent?: {
    accepted: boolean;
    acceptedAt: Timestamp;
    /** Firma gráfica del miembro (obligatoria) */
    signature?: ConsentSignature;
    /** IP/userAgent al aceptar — para trazabilidad */
    ipAddress?: string;
    userAgent?: string;
  };

  /** Firma del participante (solo adulto). Para menor es opcional. */
  participantSignature?: ConsentSignature;

  /** Firmas de padres/acudientes (solo si participantType === "minor"). Mínimo 1. */
  parentSignatures?: ConsentParentRecord[];

  /** PDF final generado, encriptado en Storage */
  pdfDocument?: ConsentDocument;

  /** Foto del participante tomada en el check-in para verificación */
  participantPhoto?: ConsentDocument;

  /** Fecha de finalización del consentimiento (todos los pasos completos) */
  completedAt?: Timestamp;

  /**
   * Invalidado porque su inscripción fue reemplazada en un intercambio de
   * integrante (swap). El doc se conserva como evidencia; no debe contar como
   * consentimiento vigente. El integrante que entró tiene su propio consent.
   */
  voidedBySwap?: boolean;
  voidedAt?: Timestamp;
}

/**
 * Item de checklist para el modal de check-in (lo que el usuario debe completar).
 */
export interface ConsentChecklistItem {
  id:
    | "idDocument"
    | "idDocumentFront"
    | "idDocumentBack"
    | "epsCertificate"
    | "medicalAndTravel"
    | "dataConsent"
    | "participationConsent"
    | "participantPhoto";
  label: string;
  done: boolean;
  required: boolean;
}

// ============================================
// NOTIFICATIONS
// ============================================

export type NotificationCategory =
  | "registration"
  | "payment"
  | "club"
  | "progress"
  | "membership"
  | "system";

export type NotificationType =
  | "registration.created"
  | "registration.approved"
  | "registration.rejected"
  | "registration.cancelled"
  | "registration.waitlist_promoted"
  | "payment.uploaded"
  | "payment.approved"
  | "payment.rejected"
  | "payment.balance_covered"
  | "joinRequest.created"
  | "joinRequest.approved"
  | "joinRequest.rejected"
  | "member.activated";

export type NotificationEmailStatus =
  | "pending"
  | "sent"
  | "skipped"
  | "failed"
  | "no_email";

export interface AppNotification {
  id: string;
  to: string;
  toEmail?: string | null;
  toName?: string | null;
  type: NotificationType;
  category: NotificationCategory;
  title: string;
  body: string;
  payload?: Record<string, any>;
  link?: string | null;
  channels: ("inApp" | "email")[];
  read: boolean;
  readAt?: Timestamp | null;
  emailStatus: NotificationEmailStatus;
  emailError?: string | null;
  emailSentAt?: Timestamp | null;
  dedupeKey?: string | null;
  createdAt: Timestamp;
}

export interface NotificationPrefs {
  emailEnabled: boolean;
  pushEnabled?: boolean;
  byCategory?: Partial<Record<NotificationCategory, boolean>>;
  byCategoryPush?: Partial<Record<NotificationCategory, boolean>>;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  emailEnabled: true,
  pushEnabled: true,
  byCategory: {
    registration: true,
    payment: true,
    club: true,
    progress: true,
    membership: true,
    system: true,
  },
  byCategoryPush: {
    registration: true,
    payment: true,
    club: true,
    progress: true,
    membership: true,
    system: true,
  },
};

export const NOTIFICATION_CATEGORY_LABEL: Record<NotificationCategory, string> =
  {
    registration: "Inscripciones",
    payment: "Pagos",
    club: "Mi club",
    progress: "Progreso",
    membership: "Membresía",
    system: "Sistema",
  };

// ============================================================================
// ACTIVIDADES DEL EVENTO (campamentos, congresos, etc.)
// ----------------------------------------------------------------------------
// Un evento contiene Categorías de Actividad (configurables por evento), y
// cada categoría contiene N Actividades. Cada Actividad tiene un modo de
// puntuación que define cómo se calculan los puntos para los clubes.
// ============================================================================

/**
 * Modo de puntuación de una actividad.
 *
 * - `podium`: 1°/2°/3°/participación según rubric de la categoría (ej. eventos
 *   espirituales, deportivos, formación, carpeta del club).
 * - `pass_fail`: cumple/no cumple → puntos fijos o 0 (ej. cumplimiento y
 *   organización).
 * - `fixed`: puntaje fijo si entrega/asiste (ej. video de eventos previos).
 * - `deductive`: empieza con un puntaje base y se restan puntos por faltas
 *   (ej. disciplina).
 * - `accumulative`: puntaje base + acumulado por participación −
 *   penalizaciones (ej. reunión de directores).
 * - `timing`: el puntaje depende de la fecha de entrega (ej. informe
 *   mensual: a tiempo vs. extra tiempo).
 */
export type ActivityScoreMode =
  | "podium"
  | "pass_fail"
  | "fixed"
  | "deductive"
  | "accumulative"
  | "timing";

/**
 * Rubric estándar de podium para una categoría: cuántos puntos vale cada
 * posición. Un valor `null`/ausente significa "no aplica" (ej. solo
 * participación).
 */
export interface PodiumRubric {
  first: number;
  second: number;
  third: number;
  participation: number;
}

/** Configuración por modo (cada categoría/actividad usa la que aplica). */
export interface ActivityScoreConfig {
  /** Para `podium`: tabla de puntos por posición. */
  podium?: PodiumRubric;
  /** Para `pass_fail`: puntos al cumplir. */
  passFailValue?: number;
  /** Para `fixed`: puntos fijos al entregar. */
  fixedValue?: number;
  /** Para `deductive`: puntaje inicial. */
  deductiveBase?: number;
  /** Para `accumulative`: base + por unidad − penalización. */
  accumulativeBase?: number;
  accumulativePerUnit?: number;
  accumulativePenaltyPerUnit?: number;
  /**
   * Para `timing`: bandas de fecha (orden importa).
   * - `untilDate`: si está presente, la banda aplica cuando la entrega
   *   ocurre antes o igual a esa fecha. La primera banda (en orden) cuyo
   *   `untilDate >= ahora` se asigna automáticamente.
   * - La última banda suele dejarse SIN `untilDate` y representa "tarde"
   *   (fallback cuando ninguna banda con fecha aplica).
   */
  timingBands?: Array<{ label: string; points: number; untilDate?: Timestamp }>;
}

/**
 * Categoría de Actividad dentro de un evento.
 *
 * Doc ID: `${eventId}_${slug}` — slug autogenerado del nombre.
 */
export interface EventActivityCategory extends AuditFields {
  id: string;
  eventId: string;
  name: string;
  slug: string;
  description?: string;
  /** Color hex o tailwind (ej. "#7c3aed" o "purple"). */
  color?: string;
  /** Nombre de ícono lucide-react (ej. "Trophy"). */
  icon?: string;
  /** Orden de visualización (ascendente). */
  order: number;
  /**
   * Modo de puntuación por defecto para actividades nuevas en esta categoría.
   * Las actividades pueden sobrescribirlo.
   */
  defaultScoreMode: ActivityScoreMode;
  /** Configuración por defecto para el modo de puntuación. */
  defaultScoreConfig: ActivityScoreConfig;
  /** Si está activa o archivada. */
  active: boolean;
}

/** Cuántos miembros del club deben participar en una actividad. */
export interface ActivityParticipantsConfig {
  /** "individual" = N personas específicas; "team" = todo el club. */
  mode: "individual" | "team";
  /** Mínimo de participantes (solo si mode = individual). */
  min?: number;
  /** Máximo de participantes (solo si mode = individual). */
  max?: number;
}

/**
 * Actividad concreta dentro de una categoría.
 *
 * Doc ID: `${eventId}_${slug}`.
 */
export interface EventActivity extends AuditFields {
  id: string;
  eventId: string;
  categoryId: string;
  name: string;
  slug: string;
  description?: string;
  /**
   * Texto largo (markdown plano) con instrucciones, requisitos,
   * recomendaciones, etc.
   */
  longDescription?: string;
  /** Modo de puntuación efectivo (puede sobrescribir el default de la categoría). */
  scoreMode: ActivityScoreMode;
  scoreConfig: ActivityScoreConfig;
  participants: ActivityParticipantsConfig;
  /** Orden dentro de la categoría. */
  order: number;
  /** Activa o archivada. */
  active: boolean;
  /** Etiquetas adicionales (ej. "previo", "espiritual", "biblia"). */
  tags?: string[];
  /**
   * Configuración de entregable previo: define qué deben enviar los
   * directores antes del evento (formulario dinámico). Si está
   * `enabled = true`, aparece en la sección "Entregables" del club.
   */
  submissionRequirements?: ActivitySubmissionRequirements;
}

/**
 * Tipo de resultado capturado por el jurado/admin para un club en una
 * actividad. Cada modo de puntuación usa un campo distinto.
 */
export type ActivityResultType =
  | "first"
  | "second"
  | "third"
  | "participation"
  | "passed"
  | "failed"
  | "delivered"
  | "not_delivered";

export interface ActivityScoreInput {
  /** Para modo `podium`. */
  position?: ActivityResultType;
  /** Para modo `pass_fail`. */
  passed?: boolean;
  /** Para modo `fixed`. */
  delivered?: boolean;
  /** Para modo `deductive`: total de faltas a descontar (puntos). */
  deductionPoints?: number;
  /** Para modo `accumulative`: # de unidades cumplidas + # de penalizaciones. */
  unitsCompleted?: number;
  penaltyUnits?: number;
  /** Para modo `timing`: índice de la banda elegida (en `timingBands`). */
  timingBandIndex?: number;
}

/**
 * Resultado de un club en una actividad concreta.
 *
 * Doc ID: `${eventId}_${activityId}_${clubId}`.
 */
export interface ClubActivityScore extends AuditFields {
  id: string;
  eventId: string;
  activityId: string;
  activityName: string;
  categoryId: string;
  categoryName: string;
  clubId: string;
  clubName: string;
  /** Datos de entrada del jurado/admin. */
  input: ActivityScoreInput;
  /** Puntos calculados por el sistema según `scoreMode` y `scoreConfig`. */
  points: number;
  /** Notas del jurado (opcional). */
  notes?: string;
  /** Quién y cuándo registró/actualizó este resultado. */
  scoredById?: string;
  scoredByName?: string;
  scoredAt?: Timestamp;
}

// ============================================================================
// ENTREGABLES PREVIOS POR CLUB (formularios dinámicos por actividad)
// ============================================================================

/**
 * Tipo de campo en un formulario de entregable.
 * - `text_short`: input de una línea
 * - `text_long`: textarea
 * - `number`: input numérico
 * - `boolean`: sí / no
 * - `select`: una opción de `options[]`
 * - `multi_select`: varias opciones
 * - `date`: fecha (YYYY-MM-DD)
 * - `url`: enlace (Drive, YouTube, etc.)
 * - `file`: un archivo en Storage
 * - `multi_file`: varios archivos
 */
export type ActivitySubmissionFieldType =
  | "text_short"
  | "text_long"
  | "number"
  | "boolean"
  | "select"
  | "multi_select"
  | "date"
  | "url"
  | "file"
  | "multi_file";

export interface ActivitySubmissionField {
  /** Slug único dentro del formulario. */
  id: string;
  /** Etiqueta visible. */
  label: string;
  /** Tipo de campo. */
  type: ActivitySubmissionFieldType;
  /** Si es obligatorio para considerar la entrega completa. */
  required: boolean;
  /** Texto de ayuda mostrado debajo del input. */
  helpText?: string;
  /** Solo para `select` / `multi_select`. */
  options?: string[];
  /** Solo `text_long`: alto del textarea (filas). */
  rows?: number;
  /** Solo `number`: min, max, step. */
  min?: number;
  max?: number;
  step?: number;
  /** Solo `file` / `multi_file`: tipos MIME aceptados (ej. ["image/*", "application/pdf"]). */
  acceptMimeTypes?: string[];
  /** Solo `multi_file`: máximo de archivos. */
  maxFiles?: number;
  /** Orden de visualización. */
  order: number;
}

export interface ActivitySubmissionRequirements {
  /** Si está activado, los clubes deben entregar este formulario. */
  enabled: boolean;
  /** Fecha de apertura: antes de esta fecha no se puede iniciar la entrega. */
  opensAt?: Timestamp;
  /** Fecha límite para entregar a tiempo. */
  deadline?: Timestamp;
  /** Permite editar/reenviar después del deadline (con marca isLate). */
  allowLate?: boolean;
  /** Campos del formulario. */
  fields: ActivitySubmissionField[];
  /** Instrucciones generales mostradas al inicio del formulario. */
  instructions?: string;
  /** Si true, al abrir el formulario (opensAt) se notificará por email a los directores. */
  notifyOnOpen?: boolean;
  /** Marca de cuándo se envió la notificación de apertura (para evitar reenvíos). */
  openNotifiedAt?: Timestamp;
}

/**
 * Valor de un campo `file` / `multi_file`.
 */
export interface SubmissionFileRef {
  url: string;
  path: string;
  name: string;
  contentType: string;
  size: number;
  uploadedAt: Timestamp;
}

export type SubmissionFieldValue =
  | string
  | number
  | boolean
  | string[]
  | SubmissionFileRef
  | SubmissionFileRef[]
  | null;

export type SubmissionStatus = "draft" | "submitted" | "approved" | "rejected";

/**
 * Entrega de un club para una actividad.
 *
 * Doc ID: `${eventId}_${activityId}_${clubId}`.
 */
export interface ClubActivitySubmission extends AuditFields {
  id: string;
  eventId: string;
  activityId: string;
  activityName: string;
  categoryId: string;
  clubId: string;
  clubName: string;
  associationId?: string;

  status: SubmissionStatus;
  /** Valores del formulario indexados por field.id. */
  values: Record<string, SubmissionFieldValue>;

  /** Quién envió por primera vez (firstSubmittedAt). */
  submittedById?: string;
  submittedByName?: string;
  submittedAt?: Timestamp;
  /** Última edición (puede coincidir con submittedAt si no se editó). */
  lastEditedById?: string;
  lastEditedByName?: string;
  lastEditedAt?: Timestamp;
  /** Cuántas veces se ha editado tras enviar. */
  editCount?: number;

  /** Calculado en el envío comparando con `deadline`. */
  isLate?: boolean;
  /** Banda timing aplicada (índice en `timingBands`) — solo timing. */
  timingBandIndex?: number;

  /** Revisión por admin. */
  reviewStatus?: "pending" | "approved" | "rejected";
  reviewedAt?: Timestamp;
  reviewedById?: string;
  reviewedByName?: string;
  reviewNotes?: string;
}
